import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSchema } from '@/hooks/useSchema';
import { useStore } from '@/store';
import type { AvailableField, ColumnConfig } from '@/types/table';
import { applyPaStatusOutputFields } from '@/utils/paStatusColumns';
import {
  extractFieldsFromSchema,
  loadColumnPreferences,
  saveColumnConfig,
} from '@/utils/table-config';

interface useColumnCustomizationReturn {
  availableFields: { inputFields: AvailableField[]; outputFields: AvailableField[] };
  columnConfig: ColumnConfig[];
  selectedFields: string[];
  handleFieldSelectionChange: (newSelection: string[]) => void;
}

/**
 * Helper function to build column config from workflow and preferences
 */
const buildColumnConfig = (
  selectedWorkflowId: string | null,
  workflow: any,
  availableFields: { inputFields: AvailableField[]; outputFields: AvailableField[] },
): { config: ColumnConfig[]; selected: string[] } => {
  if (
    !selectedWorkflowId ||
    (!availableFields?.inputFields.length && !availableFields?.outputFields.length)
  ) {
    return { config: [], selected: [] };
  }

  const savedPreferences = loadColumnPreferences(selectedWorkflowId, workflow?.workflow_type);
  const allFields = [...availableFields.inputFields, ...availableFields.outputFields];

  // Create a map of saved preferences for quick lookup
  const savedPrefsMap = new Map(savedPreferences?.map(pref => [pref.id, pref.order]) ?? []);

  // Build full column config from available fields + saved preferences
  const fullConfig: ColumnConfig[] = allFields.map((field, index) => {
    const isEnabled = savedPrefsMap.has(field.id);
    return {
      id: field.id,
      fieldPath: field.fieldPath,
      label: field.label,
      type: field.type,
      group: field.group,
      enabled: isEnabled,
      order: isEnabled ? savedPrefsMap.get(field.id)! : 999 + index, // Saved order or high number for disabled
      // Rebuilt from the schema on every render, so the localStorage preference
      // (which stores only { id, order }) needs no migration.
      cell: field.cell,
    };
  });

  const enabledFields = fullConfig.filter(c => c.enabled).map(c => c.id);
  return { config: fullConfig, selected: enabledFields };
};

/**
 * Custom hook for managing table column configuration
 * Handles field extraction, preference loading/saving, and column selection
 * Retrieves workflow from store based on workflowId
 */
export const useColumnCustomization = (): useColumnCustomizationReturn => {
  // Get workflow from store
  const { workflows, selectedWorkflowId } = useStore();
  const workflow = selectedWorkflowId ? workflows[selectedWorkflowId] || null : null;

  // Fetch schema via schema service (supports BSR fallback)
  const { inputGroups, outputGroups, isLoading: schemaLoading } = useSchema(selectedWorkflowId);

  // Extract available fields from schema (not directly from workflow.ui).
  // The PA Status workflows then get their five output columns layered on: those leaves
  // are nested inside drug[]/procedure[] on pa_status_medbv, so the top-level-only
  // extractor above can never reach them.
  const availableFields = useMemo(() => {
    if (!inputGroups.length && !outputGroups.length) {
      return { inputFields: [], outputFields: [] };
    }
    const base = extractFieldsFromSchema(inputGroups, outputGroups);
    return {
      ...base,
      outputFields: applyPaStatusOutputFields(
        workflow?.workflow_type,
        outputGroups,
        base.outputFields,
      ),
    };
  }, [inputGroups, outputGroups, workflow?.workflow_type]);

  // Initialize column configuration state with lazy initialization
  // This ensures state is populated immediately with store data, not after useEffect
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>(() => {
    const { config } = buildColumnConfig(selectedWorkflowId, workflow, availableFields);
    return config;
  });

  const [selectedFields, setSelectedFields] = useState<string[]>(() => {
    const { selected } = buildColumnConfig(selectedWorkflowId, workflow, availableFields);
    return selected;
  });

  // Update column preferences when workflow or available fields change
  // Skip building config while schema is still loading
  useEffect(() => {
    if (
      !schemaLoading &&
      selectedWorkflowId &&
      (availableFields?.inputFields.length > 0 || availableFields?.outputFields.length > 0)
    ) {
      const { config, selected } = buildColumnConfig(selectedWorkflowId, workflow, availableFields);
      setColumnConfig(config);
      setSelectedFields(selected);
    }
  }, [selectedWorkflowId, availableFields, workflow, schemaLoading]);

  // Handle field selection change - preserve selection order
  const handleFieldSelectionChange = useCallback(
    (newSelectedFields: string[]) => {
      setSelectedFields(newSelectedFields);

      // Update column config and save to localStorage
      if (selectedWorkflowId) {
        // Create a map of field ID to selection order
        const selectionOrderMap = new Map<string, number>();
        newSelectedFields.forEach((fieldId, index) => {
          selectionOrderMap.set(fieldId, index);
        });

        const updatedConfig = columnConfig.map(config => ({
          ...config,
          enabled: newSelectedFields.includes(config.id),
          // Set order based on selection order (first selected = 0, second = 1, etc.)
          // Disabled fields get order 999 to push them to the end
          order: selectionOrderMap.has(config.id) ? selectionOrderMap.get(config.id)! : 999,
        }));
        setColumnConfig(updatedConfig);
        saveColumnConfig(selectedWorkflowId, updatedConfig);
      }
    },
    [columnConfig, selectedWorkflowId],
  );

  return {
    availableFields,
    columnConfig,
    selectedFields,
    handleFieldSelectionChange,
  };
};
